import { useState, useCallback, FocusEvent } from 'react';

import { useTranslation } from 'react-i18next';
import { Patterns } from '@treezor/design-system';

import { IPostUser } from '@/features/user/store/user.model';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useAsyncInput } from '@/hooks/useAsyncInput';
interface IUseMailInput {
  key?: string;
  initialValue?: string;
  isRequired?: boolean;
  isFieldValid?: (field: string) => boolean;
}

export const useMailInput = ({ key = 'mail', initialValue, isFieldValid, isRequired }: IUseMailInput) => {
  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState(initialValue);

  const config = useDashboardConfig();

  const isValid = Patterns.email.test(inputValue) && (isFieldValid?.(inputValue) ?? true);

  const handleMail = (response) => (response?.['users']?.length > 0 ? [] : [{}]);

  const { iconStatus, status } = useAsyncInput<IPostUser>({
    key,
    inputValue,
    url: `${config.api.baseUrl}/v1/users?email=${inputValue}&userStatus=VALIDATED`,
    handleResponse: handleMail,
    shouldSkip: !isValid,
  });

  const handleAsyncBlur = useCallback((event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setInputValue(event.target.value);
  }, []);

  const getInputError = useCallback(() => {
    if (inputValue !== '' && !isValid) {
      if (!Patterns.email.test(inputValue)) {
        return t('pages.dashboard.createUser.form.rules.patternMail');
      }

      if (!isFieldValid?.(inputValue)) {
        return t('pages.dashboard.createUser.form.rules.emailAlreadyUsed');
      }
    }

    if (status === 'error') {
      return t('pages.dashboard.createUser.form.rules.emailAlreadyExists');
    }

    if (inputValue === '' && isRequired) {
      return t('pages.dashboard.createUser.form.rules.emailRequired');
    }

    return undefined;
  }, [inputValue, isValid, status, isRequired, isFieldValid, t]);

  return { iconStatus, handleAsyncBlur, error: getInputError() };
};
